import { useCallback } from 'react';
import { Stack, Tabs, Text } from '@adc/parallax-component-library';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { usePatientSearch } from '~/api/query/patientSearch';
import type { DepartmentSort, IDNSort, PatientSort } from '~/api/types';
import type { PatientTableColumnKey } from '~/components/table/patient/types';
import { USER_ROLES } from '~/features/auth/roles';
import { PatientsHeader } from '~/features/customerAdministration/patients/components/PatientsHeader';
import {
  PATIENTS_TABS,
  PatientsTabsConfig,
  type PatientsTabsValue,
} from '~/features/customerAdministration/patients/constants';
import { TabContent } from '~/features/customerAdministration/patients/tabsContent';
import { sanitizePatientSort } from '~/features/customerAdministration/patients/utils';
import type { CustomerAdministrationPatientSearchParams } from '~/types/PatientRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'customer-administration-patients';

export const Route = createFileRoute('/customer-admin/patients')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): CustomerAdministrationPatientSearchParams<
    PatientsTabsValue,
    PatientSort | DepartmentSort | IDNSort
  > => {
    // Validate and parse the search params into a typed state
    const defaultSortDir = search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;
    const filters = search.filters ?? undefined;
    const departmentId = search.departmentId
      ? (search.departmentId as string)
      : undefined;
    const tab =
      search.tab != undefined &&
      Object.values(PATIENTS_TABS).includes(search.tab as PatientsTabsValue)
        ? (search.tab as PatientsTabsValue)
        : PATIENTS_TABS.searchAllPatients;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as PatientTableColumnKey | undefined) ?? undefined,
      search: search.search ? decodeURI(search.search as string) : undefined,
      departmentId,
      filters,
      sortDir,
      tab,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const {
    page = 1,
    search = '',
    sort = '',
    sortDir = 'desc',
    filters,
    tab = PATIENTS_TABS.searchAllPatients,
    IDN,
    IDNId,
    department,
    departmentId,
  } = Route.useSearch();

  const { data } = usePatientSearch({
    page,
    search: search === '' ? undefined : search,
    sort: sanitizePatientSort(sort),
    sortDir,
    filters,
  });

  const navigate = useNavigate({ from: Route.fullPath });

  const onBreadCrumbPress = useCallback(
    () =>
      void navigate({
        to: Route.fullPath,
        search: {
          tab,
        },
      }),
    [navigate, tab],
  );

  return (
    <Stack testID={TEST_ID}>
      <PatientsHeader
        testID={buildTestId({ testID: TEST_ID, suffix: 'header' })}
        onBreadCrumbPress={onBreadCrumbPress}
        IDN={IDN}
        IDNId={IDNId}
        department={department}
        departmentId={departmentId}
      />

      <Stack marginVertical="$2" flexDirection="row" alignItems="flex-end">
        <Stack flex={1}>
          <Tabs
            tabs={PatientsTabsConfig}
            selectedIndex={tab}
            onSelect={(index) =>
              void navigate({
                search: () => ({ tab: index as PatientsTabsValue }),
              })
            }
            testID={buildTestId({ testID: TEST_ID, suffix: 'tabs' })}
          />
        </Stack>
        <Text
          testID={buildTestId({ testID: TEST_ID, suffix: ['last', 'sync'] })}
          flex={1}
          textAlign="right"
          type="$labelBase.medium"
          paddingRight="$2"
        >
          Last sync:{' '}
          {data?.lastSync
            ? new Date(data.lastSync).toUTCString()
            : 'unavailable'}
        </Text>
      </Stack>
      <TabContent
        testID={buildTestId({ testID: TEST_ID, suffix: ['tabs', 'content'] })}
        activeTab={tab}
      />
    </Stack>
  );
}
